import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { FC, MouseEvent, MouseEventHandler, ReactNode } from 'react';
import { useState } from 'react';

import type { ConversionCreditWithAmounts } from '@sorare/core/src/hooks/useConversionCredits';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import { useLoggedCallback } from '@sorare/core/src/hooks/useLoggedCallback';
import { useMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';
import { useBeforeSubmitPaymentCallback } from '@sorare/core/src/hooks/wallets/useHandleWalletStateBeforePayment';
import { useEvents } from '@sorare/core/src/lib/events/useEvents';
import type { ClickInstantBuy_Flow } from '@sorare/core/src/protos/events/so5/shared/events';

import type { PrimaryBuyConfirmationOptions } from 'contexts/buyingConfirmation';

import { PrimaryBuyFlow } from '../PrimaryBuyFlow';
import type { WebPrimaryOfferBuyFieldWrapper_anyCard } from './__generated__/index.graphql';

export interface Props {
  card: WebPrimaryOfferBuyFieldWrapper_anyCard;
  primaryBuyConfirmationOptions?: PrimaryBuyConfirmationOptions;
  onSuccess?: () => void;
  onBuyConfirmationClose?: () => void;
  origin: ClickInstantBuy_Flow;
  gameSlug?: string;
  showBuyConfirmation?: boolean;
  onlyCreditCards?: boolean;
  conversionCreditDisclaimer?: ReactNode;
  defaultConversionCredit?: ConversionCreditWithAmounts;
  applyConversionCredits?: boolean;
  composeSessionId?: string;
  children: FC<
    React.PropsWithChildren<{
      onClick: MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
      loading: boolean;
      disabled?: boolean;
    }>
  >;
  onClick?: MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
  rosterId?: string;
}

export const WebPrimaryOfferBuyFieldWrapper = ({
  children,
  card,
  primaryBuyConfirmationOptions,
  onSuccess,
  onBuyConfirmationClose,
  origin,
  gameSlug,
  showBuyConfirmation = true,
  onlyCreditCards = false,
  conversionCreditDisclaimer,
  defaultConversionCredit,
  applyConversionCredits = false,
  onClick,
  composeSessionId = '',
  rosterId,
}: Props) => {
  const {
    isMarketAuthorizedIosApp,
    postMessage,
    canOpenCartOpenPaymentBoxAndCloseCurrentWebview,
  } = useIsMobileApp();
  const canOpenPaymentBox =
    isMarketAuthorizedIosApp && canOpenCartOpenPaymentBoxAndCloseCurrentWebview;
  const beforeSubmitPaymentCallback = useBeforeSubmitPaymentCallback();

  const primaryOffer = card.livePrimaryOffer;
  const { id, price, signedAmount } = primaryOffer || {};
  const { toMonetaryAmount } = useMonetaryAmount();
  const [paymentStarted, setPaymentStarted] = useState(false);

  const track = useEvents();
  const loggedTogglePaymentStarted = useLoggedCallback<boolean>(b => {
    if (isMarketAuthorizedIosApp && canOpenPaymentBox) {
      postMessage('openPaymentBox');
    }
    setPaymentStarted(b);
  });

  const priceMonetaryAmount = toMonetaryAmount(price);

  if (!price || !signedAmount || !id) return null;

  const onBuyButtonClick = (
    e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>
  ) => {
    track('Click Instant Buy', {
      offerId: id,
      cardSlug: card.slug,
      ethAmount: priceMonetaryAmount.eth,
      eurAmount: priceMonetaryAmount.eur,
      gameSlug,
      origin,
      rosterId,
      scarcity: card.rarityTyped,
      sport: card.sport,
      composeSessionId,
    });
    loggedTogglePaymentStarted(true);
    onClick?.(e);
  };

  return (
    <>
      {children({
        loading: paymentStarted,
        onClick: onBuyButtonClick,
      })}
      {!!paymentStarted && (
        <PrimaryBuyFlow
          offerId={id}
          onSuccess={onSuccess}
          onClose={() => setPaymentStarted(false)}
          defaultConversionCredit={defaultConversionCredit}
          primaryBuyConfirmationOptions={primaryBuyConfirmationOptions}
          onBuyConfirmationClose={onBuyConfirmationClose}
          conversionCreditDisclaimer={conversionCreditDisclaimer}
          onlyCreditCards={onlyCreditCards}
          showBuyConfirmation={showBuyConfirmation}
          applyConversionCredits={applyConversionCredits}
          beforeSubmitCallback={beforeSubmitPaymentCallback}
        />
      )}
    </>
  );
};

WebPrimaryOfferBuyFieldWrapper.fragments = {
  anyCard: gql`
    fragment WebPrimaryOfferBuyFieldWrapper_anyCard on AnyCardInterface {
      slug
      sport
      rarityTyped
      livePrimaryOffer {
        id
        price {
          ...useMonetaryAmount_monetaryAmount
        }
        signedAmount
      }
    }
    ${useMonetaryAmount.fragments.monetaryAmount}
  ` as TypedDocumentNode<WebPrimaryOfferBuyFieldWrapper_anyCard>,
};
